import React from "react";
import { CAMP_DAY_LABELS } from "../lib/api";
import { useServiceActions, trainingDays, DAYS } from "../hooks/useServiceActions";

function chipCls(disabled, active) {
  if (disabled) return "opacity-40 cursor-not-allowed border-slate-200 bg-slate-50";
  if (active) return "border-[#008057] bg-[#008057] text-white";
  return "border-[#E2EBF0] bg-white text-slate-600 hover:border-[#008057]";
}

function DayChip({ day, count, max, onClick, disabled, testid }) {
  const active = count > 0;
  return (
    <button type="button" data-testid={testid} disabled={disabled} onClick={onClick}
      className={`relative rounded-xl border-2 px-3 py-2 text-sm font-semibold transition-all ${chipCls(disabled, active)}`}>
      {CAMP_DAY_LABELS[day]}
      {max > 1 && active && (
        <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#F3C242] text-[#03281B] text-[11px] font-extrabold">{count}</span>
      )}
    </button>
  );
}

const TypeRadio = ({ name, value, current, label, price, onChange, testid }) => (
  <label data-testid={testid}
    className={`flex items-center justify-between gap-3 rounded-xl border-2 px-4 py-3 cursor-pointer transition-all ${current === value ? "border-[#008057] bg-[#F2F8F5]" : "border-[#E2EBF0] bg-white hover:border-[#008057]/40"}`}>
    <div className="flex items-center gap-3">
      <input type="radio" name={name} checked={current === value} onChange={() => onChange(value)} className="accent-[#008057]" />
      <span className="text-sm font-semibold text-[#03281B]">{label}</span>
    </div>
    {price != null && <span className="font-display font-bold text-[#008057]">{price}</span>}
  </label>
);

const TRAINING_LABELS = { football: "Individual Soccer Training", goalkeeper: "Individual Goalkeeper Training" };
const TRAINING_OPTIONS = [
  ["none", "Not selected", null, (k) => `${k}-type-none`],
  ["individual", "Individual sessions", "$100 ea", (k) => `checkbox-service-${k}`],
  ["daily", "Daily Package (5)", "$350", (k) => `${k}-type-daily`],
  ["double", "Double Daily (10)", "$500", (k) => `${k}-type-double`],
];

const TrainingSection = ({ k, s, uidKey, actions }) => (
  <div>
    <h4 className="font-bold text-[#03281B] mb-1">{TRAINING_LABELS[k]}</h4>
    <p className="text-xs text-slate-500 mb-3">40-minute sessions. Maximum 2 individual sessions per day.</p>
    <div className="grid sm:grid-cols-2 gap-2">
      {TRAINING_OPTIONS.map(([v, label, price, tid]) => (
        <TypeRadio key={v} name={`${k}-${uidKey}`} value={v} current={s.type} label={label} price={price} onChange={(t) => actions.setTrainingType(k, t)} testid={tid(k)} />
      ))}
    </div>
    {s.type === "individual" && (
      <div className="mt-3">
        <p className="text-xs text-slate-500 mb-2">Tap a day to add a session (tap again for 2, once more to clear):</p>
        <div className="flex flex-wrap gap-2">
          {DAYS.map((d) => <DayChip key={d} day={d} max={2} count={s.days?.[d] || 0} onClick={() => actions.cycleDay(k, d, 2)} testid={`${k}-day-${d}`} />)}
        </div>
      </div>
    )}
  </div>
);

const DevelopmentSection = ({ on, toggle }) => (
  <div>
    <h4 className="font-bold text-[#03281B] mb-1">Complete Personalized Development Package</h4>
    <p className="text-xs text-slate-500 mb-3">Premium: 5 daily feedback sessions + written report + video analysis + individual development plan.</p>
    <label data-testid="checkbox-service-development" className={`flex items-center justify-between gap-3 rounded-xl border-2 px-4 py-3 cursor-pointer ${on ? "border-[#F3C242] bg-amber-50" : "border-[#E2EBF0] bg-white"}`}>
      <div className="flex items-center gap-3">
        <input type="checkbox" checked={on} onChange={toggle} className="accent-[#008057] w-4 h-4" />
        <span className="text-sm font-semibold text-[#03281B]">Add Development Package</span>
      </div>
      <span className="font-display font-bold text-[#008057]">$250</span>
    </label>
  </div>
);

const FEEDBACK_OPTIONS = [["none", "Not selected", null], ["individual", "Individual", "$50 ea"], ["daily", "Daily (5)", "$150"]];

const FeedbackSection = ({ fb, dev, uidKey, actions }) => (
  <div className={dev ? "opacity-50 pointer-events-none" : ""}>
    <h4 className="font-bold text-[#03281B] mb-1">Personalized Feedback with Written Report</h4>
    <p className="text-xs text-slate-500 mb-3">Maximum one feedback session per day. {dev && "Already included in the Development Package."}</p>
    <div className="grid sm:grid-cols-3 gap-2">
      {FEEDBACK_OPTIONS.map(([v, label, price]) => (
        <TypeRadio key={v} name={`fb-${uidKey}`} value={v} current={fb.type} label={label} price={price} onChange={actions.toggleFeedbackType} testid={`feedback-type-${v}`} />
      ))}
    </div>
    {fb.type === "individual" && (
      <div className="flex flex-wrap gap-2 mt-3">
        {DAYS.map((d) => <DayChip key={d} day={d} max={1} count={fb.days?.[d] || 0} onClick={() => actions.cycleFeedbackDay(d)} testid={`feedback-day-${d}`} />)}
      </div>
    )}
  </div>
);

const MealsSection = ({ svc, dietary, td, actions }) => (
  <div>
    <h4 className="font-bold text-[#03281B] mb-1">Meal Service &middot; $30/day</h4>
    <p className="text-xs text-slate-500 mb-3">
      {td.size === 0
        ? "Meal service is exclusively available to participants who have booked individual soccer or goalkeeper training on the selected day."
        : "Select meal days (only days with a soccer or goalkeeper training booking are available). 5-day total: $150."}
    </p>
    <div className="flex flex-wrap gap-2">
      {DAYS.map((d) => <DayChip key={d} day={d} max={1} count={(svc.meals.days || []).includes(d) ? 1 : 0} disabled={!td.has(d)} onClick={() => actions.toggleMeal(d)} testid={`meal-day-${d}`} />)}
    </div>
    <textarea data-testid="meal-dietary" value={dietary} onChange={(e) => actions.setDietary(e.target.value)}
      placeholder="Food allergies or dietary restrictions (optional). Meals are enjoyed together with our coaches and camp staff."
      className="mt-3 w-full rounded-xl border-2 border-[#E2EBF0] px-3 py-2 text-sm focus:border-[#008057] outline-none" rows={2} />
  </div>
);

export default function ServicesEditor({ participant, onChange }) {
  const svc = participant.services;
  const actions = useServiceActions(participant, onChange);
  return (
    <div className="space-y-8">
      {["football", "goalkeeper"].map((k) => <TrainingSection key={k} k={k} s={svc[k]} uidKey={participant._k} actions={actions} />)}
      <DevelopmentSection on={svc.development_package} toggle={actions.toggleDevelopment} />
      <FeedbackSection fb={svc.feedback} dev={svc.development_package} uidKey={participant._k} actions={actions} />
      <MealsSection svc={svc} dietary={participant.meal_dietary} td={trainingDays(svc)} actions={actions} />
    </div>
  );
}
